import React, { FC } from 'react'
import { styled, breakpointDown } from '@moonpig/launchpad-utils'
import { Box, Text, Container } from '@moonpig/launchpad-components'
import { removeSearchHistoryItem } from '@moonpig/web-core-app-shell-search'
import { system as s } from '@moonpig/launchpad-system'
import { ZeroResultsHeader } from '../ZeroResultsHeader/ZeroResultsHeader'
import { DepartmentHeaders } from './DepartmentHeaders'
import { useFindLocaleText } from '../../text-localisation'
import { ContactUsCarousel } from '../ContactUsCarousel'
import type { SearchContext } from '../../store/slices/shared'
import { useSearchStore } from '../../store/SearchStore'

const StyledText = styled(Text)`
  font-weight: bold;
  font-style: italic;
`

const ReservedSpace = styled(Box)`
  height: 24px;
  ${breakpointDown('md')} {
    height: 64px;
  }
`

const StyledContainer = styled(Container)`
  ${s({
    mb: 5,
    p: 0,
    marginX: { xs: 6, lg: 10, xxl: 12 },
  })}
`

export type SearchHeaderProps = {
  zeroResults: boolean
  searchContext: SearchContext
  totalCount: number
}

export const SearchHeader: FC<SearchHeaderProps> = ({
  zeroResults,
  searchContext,
  totalCount,
}) => {
  const loadingProducts = useSearchStore(store => store.loading)
  const localiseText = useFindLocaleText()

  const {
    departments,
    searchTerm: originalSearchTerm,
    correctedSearchTerm,
  } = searchContext

  const searchTerm = correctedSearchTerm || originalSearchTerm
  const department = departments[0]

  if (zeroResults && department && !loadingProducts) {
    removeSearchHistoryItem({
      searchTerm,
      department: department.toLowerCase(),
    })
  }

  return (
    <StyledContainer data-testid="search-header">
      <Box paddingX={0}>
        {loadingProducts ? (
          <ReservedSpace />
        ) : (
          <>
            {zeroResults ? (
              <ZeroResultsHeader searchContext={searchContext} />
            ) : (
              <>
                <DepartmentHeaders
                  totalCount={totalCount}
                  searchContext={searchContext}
                />

                {correctedSearchTerm && (
                  <Box marginTop={5} data-testid="spelling-correction">
                    <Text as="p">
                      {localiseText('find.there_are_no_results_for')}
                      <StyledText>{originalSearchTerm}</StyledText>
                    </Text>
                  </Box>
                )}
                <ContactUsCarousel searchTerm={searchTerm} />
              </>
            )}
          </>
        )}
      </Box>
    </StyledContainer>
  )
}
